import { useState, useMemo, useCallback, useEffect, useRef, type ChangeEvent } from 'react';
import { X, Upload, FileUp, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiClient } from '@/api/client';
import { useToastStore } from '@/stores/toast-store';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface ImportModalProps {
  connectionId: string;
  schema: string;
  tableName?: string; // If importing into existing table
  tableColumns?: { name: string; type: string }[]; // Columns of existing table
  onClose: () => void;
  onImported: () => void;
}

interface ColumnMapping {
  fileColumn: string;
  tableColumn: string | null; // null = skip
  detectedType: string; // for new table mode
}

interface ParsedData {
  headers: string[];
  rows: string[][]; // All rows as string arrays (normalized from CSV or JSON)
}

type OnErrorAction = 'skip' | 'abort';

// ═══════════════════════════════════════════
// CSS Classes (matching ExportModal style)
// ═══════════════════════════════════════════

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2 py-[5px] text-[12px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const selectClass =
  'w-full bg-dbgo-input border rounded-sm px-2 py-[5px] text-[12px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer';

const labelClass = 'block text-[11px] text-text-muted mb-1';

// ═══════════════════════════════════════════
// Parsing Utilities
// ═══════════════════════════════════════════

function parseCSV(text: string): ParsedData {
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  // Detect delimiter
  const firstLine = lines[0];
  const delimiters = [',', ';', '\t', '|'];
  const delimiter = delimiters.reduce(
    (best, d) => (firstLine.split(d).length > firstLine.split(best).length ? d : best),
    ',',
  );

  // Simple CSV parsing (handles quoted fields)
  function parseLine(line: string): string[] {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === delimiter && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  }

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(parseLine);
  return { headers, rows };
}

function parseJSON(text: string): ParsedData {
  const data: unknown = JSON.parse(text);
  const arr = Array.isArray(data) ? data : [data];
  if (arr.length === 0) return { headers: [], rows: [] };
  const headers = Object.keys(arr[0] as Record<string, unknown>);
  const rows = arr.map((item) => {
    const record = item as Record<string, unknown>;
    return headers.map((h) => {
      const v = record[h];
      if (v === null || v === undefined) return '';
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    });
  });
  return { headers, rows };
}

// ═══════════════════════════════════════════
// Type Detection
// ═══════════════════════════════════════════

function detectColumnType(values: string[]): string {
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (nonNull.length === 0) return 'text';
  if (nonNull.every((v) => /^-?\d+$/.test(v))) return 'integer';
  if (nonNull.every((v) => /^-?\d+\.?\d*$/.test(v))) return 'numeric';
  if (nonNull.every((v) => /^(true|false)$/i.test(v))) return 'boolean';
  if (nonNull.every((v) => !isNaN(Date.parse(v)) && v.length > 4)) return 'timestamp';
  return 'text';
}

// ═══════════════════════════════════════════
// SQL Generation
// ═══════════════════════════════════════════

function generateInsertSQL(
  schema: string,
  table: string,
  columns: string[],
  rows: (string | null)[][],
  batchSize: number,
): string[] {
  const statements: string[] = [];
  const colList = columns.map((c) => `"${c}"`).join(', ');

  for (let i = 0; i < rows.length; i += batchSize) {
    const batch = rows.slice(i, i + batchSize);
    const values = batch.map(
      (row) =>
        `(${row.map((v) => (v === null || v === '' ? 'NULL' : `'${String(v).replace(/'/g, "''")}'`)).join(', ')})`,
    );
    statements.push(
      `INSERT INTO "${schema}"."${table}" (${colList}) VALUES\n${values.join(',\n')};`,
    );
  }

  return statements;
}

function generateCreateTableSQL(
  schema: string,
  table: string,
  columns: { name: string; type: string }[],
): string {
  const lines = columns.map((c) => `  "${c.name}" ${c.type}`);
  return `CREATE TABLE "${schema}"."${table}" (\n${lines.join(',\n')}\n);`;
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function ImportModal({
  connectionId,
  schema,
  tableName,
  tableColumns,
  onClose,
  onImported,
}: ImportModalProps) {
  const addToast = useToastStore((s) => s.addToast);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File state
  const [fileName, setFileName] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [parseError, setParseError] = useState<string>('');

  // Target mode
  const [targetMode, setTargetMode] = useState<'existing' | 'new'>(tableName ? 'existing' : 'new');
  const [newTableName, setNewTableName] = useState('');

  // Column mappings
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);

  // Options
  const [batchSize, setBatchSize] = useState(100);
  const [onError, setOnError] = useState<OnErrorAction>('skip');

  // Import state
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importError, setImportError] = useState<string>('');

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, importing]);

  // Auto-generate mappings when parsed data or target mode changes
  useEffect(() => {
    if (!parsedData) return;

    const newMappings: ColumnMapping[] = parsedData.headers.map((header) => {
      // Detect column type from sample data
      const sampleValues = parsedData.rows.slice(0, 100).map((row) => {
        const idx = parsedData.headers.indexOf(header);
        return row[idx] ?? '';
      });
      const detectedType = detectColumnType(sampleValues);

      // Auto-match to existing table column by name
      let tableColumn: string | null = null;
      if (targetMode === 'existing' && tableColumns) {
        const match = tableColumns.find(
          (tc) => tc.name.toLowerCase() === header.toLowerCase(),
        );
        if (match) tableColumn = match.name;
      } else if (targetMode === 'new') {
        // In new table mode, map all columns by default
        tableColumn = header;
      }

      return { fileColumn: header, tableColumn, detectedType };
    });

    setMappings(newMappings);
  }, [parsedData, targetMode, tableColumns]);

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError('');
    setImportError('');

    // Handle Excel files (.xlsx, .xls)
    if (/\.xlsx?$/i.test(file.name)) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const arrayData = new Uint8Array(reader.result as ArrayBuffer);
          const wb = XLSX.read(arrayData, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          if (!ws) {
            setParseError('Excel file has no sheets');
            setParsedData(null);
            return;
          }
          const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws);
          if (jsonData.length === 0) {
            setParseError('Excel file is empty');
            setParsedData(null);
            return;
          }
          const headers = Object.keys(jsonData[0]);
          const rows = jsonData.map((row) =>
            headers.map((h) => {
              const v = row[h];
              if (v === null || v === undefined) return '';
              return String(v);
            }),
          );
          setParsedData({ headers, rows });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to parse Excel file';
          setParseError(msg);
          setParsedData(null);
        }
      };
      reader.readAsArrayBuffer(file);
      // Reset so the same file can be selected again
      e.target.value = '';
      return;
    }

    // Handle text-based files (CSV, TSV, JSON)
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      try {
        let data: ParsedData;
        if (file.name.endsWith('.json')) {
          data = parseJSON(text);
        } else {
          data = parseCSV(text);
        }
        if (data.headers.length === 0) {
          setParseError('No columns detected in file');
          setParsedData(null);
          return;
        }
        setParsedData(data);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to parse file';
        setParseError(msg);
        setParsedData(null);
      }
    };
    reader.readAsText(file);
    // Reset so the same file can be selected again
    e.target.value = '';
  }, []);

  const updateMapping = useCallback(
    (index: number, tableColumn: string | null) => {
      setMappings((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], tableColumn };
        return next;
      });
    },
    [],
  );

  // Count mapped columns (non-skipped)
  const activeMappings = useMemo(
    () => mappings.filter((m) => m.tableColumn !== null),
    [mappings],
  );

  // Determine the effective target table name
  const effectiveTableName = targetMode === 'existing' ? (tableName ?? '') : newTableName;

  // Can import?
  const canImport =
    parsedData !== null &&
    activeMappings.length > 0 &&
    effectiveTableName.trim().length > 0 &&
    !importing;

  const handleImport = useCallback(async () => {
    if (!parsedData || !canImport) return;

    setImporting(true);
    setProgress(0);
    setImportError('');

    try {
      const targetTable = effectiveTableName.trim();

      // Build mapped rows
      const mappedColumns: string[] = [];
      const fileColumnIndices: number[] = [];

      for (const m of mappings) {
        if (m.tableColumn === null) continue;
        mappedColumns.push(m.tableColumn);
        fileColumnIndices.push(parsedData.headers.indexOf(m.fileColumn));
      }

      const mappedRows = parsedData.rows.map((row) =>
        fileColumnIndices.map((idx) => {
          const val = row[idx];
          return val === undefined || val === '' ? null : val;
        }),
      );

      // If new table mode, create the table first
      if (targetMode === 'new') {
        const newTableColumns = mappings
          .filter((m) => m.tableColumn !== null)
          .map((m) => ({
            name: m.tableColumn as string,
            type: m.detectedType,
          }));

        const createSQL = generateCreateTableSQL(schema, targetTable, newTableColumns);
        await apiClient.post('/api/query/execute', {
          connectionId,
          sql: createSQL,
        });
      }

      // Generate INSERT batches
      const batches = generateInsertSQL(
        schema,
        targetTable,
        mappedColumns,
        mappedRows,
        batchSize,
      );

      // Execute batches sequentially
      let importedRows = 0;
      let skippedBatches = 0;
      for (let i = 0; i < batches.length; i++) {
        try {
          await apiClient.post('/api/query/execute', {
            connectionId,
            sql: batches[i],
          });
          const batchRowCount = Math.min(batchSize, mappedRows.length - i * batchSize);
          importedRows += batchRowCount;
        } catch (err: unknown) {
          if (onError === 'abort') {
            const msg = err instanceof Error ? err.message : 'Import failed';
            throw new Error(`Batch ${i + 1} failed: ${msg}. ${importedRows} rows imported before error.`);
          }
          skippedBatches++;
        }
        setProgress(Math.round(((i + 1) / batches.length) * 100));
      }

      const message = skippedBatches > 0
        ? `Imported ${importedRows} rows (${skippedBatches} batch(es) skipped due to errors)`
        : `Imported ${importedRows} rows into ${schema}.${targetTable}`;
      addToast('success', message);
      onImported();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setImportError(msg);
      addToast('error', msg);
    } finally {
      setImporting(false);
    }
  }, [
    parsedData,
    canImport,
    effectiveTableName,
    mappings,
    targetMode,
    schema,
    connectionId,
    batchSize,
    onError,
    addToast,
    onImported,
  ]);

  // Preview rows (first 10)
  const previewRows = useMemo(() => {
    if (!parsedData) return [];
    return parsedData.rows.slice(0, 10);
  }, [parsedData]);

  // Only show mapped headers in preview
  const previewHeaders = useMemo(() => {
    return activeMappings.map((m) => m.fileColumn);
  }, [activeMappings]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={importing ? undefined : onClose}
    >
      <div
        className="w-[700px] max-h-[85vh] flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-sm font-semibold text-text-primary">Import Data</h2>
            {tableName && (
              <span className="text-[11px] text-text-muted">
                Target: {schema}.{tableName}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-3">
          {/* File Selection */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-text-secondary border rounded-sm hover:text-text-primary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-50"
            >
              <FileUp size={13} />
              Choose File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.json,.xlsx,.xls"
              className="hidden"
              onChange={handleFileSelect}
            />
            {fileName && (
              <span className="text-[12px] text-text-secondary">
                {fileName}
                {parsedData && (
                  <span className="text-text-muted ml-1">
                    ({parsedData.rows.length.toLocaleString()} rows, {parsedData.headers.length} columns)
                  </span>
                )}
              </span>
            )}
            {parseError && (
              <span className="text-[12px] text-accent-red flex items-center gap-1">
                <AlertCircle size={12} />
                {parseError}
              </span>
            )}
          </div>

          {/* Target Selection */}
          {parsedData && (
            <div className="flex items-center gap-4">
              <span className={labelClass}>Target:</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="targetMode"
                  checked={targetMode === 'existing'}
                  onChange={() => setTargetMode('existing')}
                  disabled={importing || !tableName}
                  className="accent-accent-blue"
                />
                <span className="text-[12px] text-text-secondary">Existing table</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="targetMode"
                  checked={targetMode === 'new'}
                  onChange={() => setTargetMode('new')}
                  disabled={importing}
                  className="accent-accent-blue"
                />
                <span className="text-[12px] text-text-secondary">New table</span>
              </label>

              {targetMode === 'existing' && tableName && (
                <span className="text-[12px] text-text-muted">
                  {schema}.{tableName}
                </span>
              )}
              {targetMode === 'new' && (
                <input
                  type="text"
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  placeholder="new_table_name"
                  disabled={importing}
                  className={`${inputClass} w-48`}
                />
              )}
            </div>
          )}

          {/* Column Mapping */}
          {parsedData && mappings.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <div className="px-3 py-1.5 border-b bg-dbgo-panel">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wide">
                  Column Mapping
                </span>
              </div>
              <div className="max-h-[180px] overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="border-b bg-dbgo-panel/50">
                      <th className="text-left px-3 py-1.5 text-text-muted font-medium">File Column</th>
                      <th className="text-center px-1 py-1.5 text-text-muted font-medium w-8">&rarr;</th>
                      <th className="text-left px-3 py-1.5 text-text-muted font-medium">Table Column</th>
                      <th className="text-left px-3 py-1.5 text-text-muted font-medium w-24">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((m, idx) => (
                      <tr key={m.fileColumn} className="border-b last:border-b-0">
                        <td className="px-3 py-1.5 text-text-secondary font-mono text-[11px]">
                          {m.fileColumn}
                        </td>
                        <td className="text-center px-1 py-1.5 text-text-muted">&rarr;</td>
                        <td className="px-3 py-1.5">
                          {targetMode === 'existing' && tableColumns ? (
                            <select
                              value={m.tableColumn ?? '__skip__'}
                              onChange={(e) =>
                                updateMapping(idx, e.target.value === '__skip__' ? null : e.target.value)
                              }
                              disabled={importing}
                              className={`${selectClass} text-[11px]`}
                            >
                              <option value="__skip__">(skip)</option>
                              {tableColumns.map((tc) => (
                                <option key={tc.name} value={tc.name}>
                                  {tc.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={m.tableColumn ?? ''}
                              onChange={(e) =>
                                updateMapping(idx, e.target.value || null)
                              }
                              disabled={importing}
                              placeholder="(skip)"
                              className={`${inputClass} text-[11px] font-mono`}
                            />
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-text-muted text-[11px]">
                          {targetMode === 'existing' && tableColumns ? (
                            (() => {
                              const match = tableColumns.find((tc) => tc.name === m.tableColumn);
                              return match ? (
                                <span className="text-accent-green">{match.type}</span>
                              ) : (
                                <span>-</span>
                              );
                            })()
                          ) : (
                            m.tableColumn ? (
                              <span className="text-accent-blue">{m.detectedType}</span>
                            ) : (
                              <span>-</span>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Preview */}
          {parsedData && previewHeaders.length > 0 && previewRows.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <div className="px-3 py-1.5 border-b bg-dbgo-panel">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wide">
                  Preview (first {Math.min(10, parsedData.rows.length)} rows)
                </span>
              </div>
              <div className="max-h-[150px] overflow-auto">
                <table className="w-full text-[11px] font-mono">
                  <thead>
                    <tr className="border-b bg-dbgo-panel/50">
                      {previewHeaders.map((h) => (
                        <th
                          key={h}
                          className="text-left px-2 py-1 text-text-muted font-medium whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, ri) => (
                      <tr key={ri} className="border-b last:border-b-0">
                        {previewHeaders.map((h) => {
                          const colIdx = parsedData.headers.indexOf(h);
                          const val = row[colIdx] ?? '';
                          return (
                            <td
                              key={h}
                              className="px-2 py-1 text-text-secondary whitespace-nowrap max-w-[200px] truncate"
                              title={val}
                            >
                              {val || <span className="text-text-muted italic">NULL</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Options */}
          {parsedData && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className={labelClass}>Batch size:</label>
                <input
                  type="number"
                  value={batchSize}
                  onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value, 10) || 100))}
                  min={1}
                  max={10000}
                  disabled={importing}
                  className={`${inputClass} w-20`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className={labelClass}>On error:</label>
                <select
                  value={onError}
                  onChange={(e) => setOnError(e.target.value as OnErrorAction)}
                  disabled={importing}
                  className={`${selectClass} w-28`}
                >
                  <option value="skip">Skip batch</option>
                  <option value="abort">Abort</option>
                </select>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {importing && (
            <div className="space-y-1">
              <div className="w-full h-2 bg-dbgo-input rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent-blue rounded-full transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[11px] text-text-muted">
                Importing... {progress}%
              </span>
            </div>
          )}

          {/* Import Error */}
          {importError && (
            <div className="flex items-start gap-2 px-3 py-2 border border-accent-red/30 bg-accent-red/5 rounded-sm">
              <AlertCircle size={14} className="text-accent-red mt-0.5 flex-shrink-0" />
              <span className="text-[12px] text-accent-red">{importError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t">
          <span className="text-[11px] text-text-muted">
            {parsedData
              ? `${activeMappings.length} column(s) mapped, ${parsedData.rows.length.toLocaleString()} row(s) to import`
              : 'Select a CSV, JSON, or Excel file to import'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={importing}
              className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-text-secondary border rounded-sm hover:text-text-primary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={!canImport}
              className="flex items-center gap-1.5 px-4 py-[6px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={12} />
              Import {parsedData ? `${parsedData.rows.length.toLocaleString()} rows` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
